import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";

import { exportTemplateAnalyticsExport } from "services";
import { getInterviewPerformance } from "services/analyticsV2";
import StaticTable from "components/Tables/StaticTable";
import Pagination from "components/Pagination";
import { getAverageScore } from "utils";
import { CASE_CATEGORIES } from "components/Analytics/constant";
import { Box } from "@mui/material";
import { IconButton } from "components/CustomButtons";
import { useTheme } from "@emotion/react";

const formatNumberOrDash = (value, options = {}) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  const { decimals } = options;
  const num = Number(value);
  return typeof decimals === 'number' ? num.toFixed(decimals) : Math.round(num);
};

const commonProperties = {
  type: "number",
  renderCell: ({ row, field }) => formatNumberOrDash(row?.[field]),
  align: "center",
};

const buildColumns = (interviewType) => {
  const base = [
    { field: "name", label: "Name", type: "string", minWidth: 160, flex: 1, fixed: 1, disableSorting: true },
    {
      field: "overallScore",
      type: "number",
      ...commonProperties,
      label: "Overall Score",
      minWidth: 120,
      sort: true,
      disableSorting: true,
    },
  ];

  if (String(interviewType || '').startsWith('case')) {
    return [
      { field: "name", label: "Name", type: "string", minWidth: 160, flex: 1, fixed: 1, disableSorting: true },
      {
        field: "overallScore",
        label: "Overall Score",
        minWidth: 130,
        align: 'center',
        type: 'number',
        disableSorting: true,
        // Overall is 0-10, so whole numbers would collapse the whole range.
        renderCell: ({ row }) => formatNumberOrDash(row?.overallScore, { decimals: 2 }),
      },
      ...CASE_CATEGORIES.map(({ id, label }) => ({
        field: id, label, ...commonProperties, minWidth: 170, disableSorting: true,
      })),
      { field: "highestScore", label: "Highest Score", minWidth: 130, align: 'center', type: 'number', disableSorting: true, renderCell: ({ row }) => formatNumberOrDash(row?.highestScore, { decimals: 2 }) },
      { field: "lowestScore", label: "Lowest Score", minWidth: 130, align: 'center', type: 'number', disableSorting: true, renderCell: ({ row }) => formatNumberOrDash(row?.lowestScore, { decimals: 2 }) },
      { field: "finishedAttempts", label: "Finished Attempts", ...commonProperties, minWidth: 150, disableSorting: true },
      { field: "unfinishedAttempts", label: "Unfinished Attempts", ...commonProperties, minWidth: 160, disableSorting: true },
      { field: "droppedMidSession", label: "Dropped Mid-session", ...commonProperties, minWidth: 170, disableSorting: true },
      { field: "proctoredIssues", label: "Proctored Issues", ...commonProperties, minWidth: 150, disableSorting: true },
      { field: "averageTimeTaken", label: "Avg. Time Taken (min)", minWidth: 180, align: 'center', type: 'number', disableSorting: true, renderCell: ({ row }) => formatNumberOrDash(row?.averageTimeTaken, { decimals: 1 }) },
      { field: "mostRecentAttemptAt", label: "Most Recent Attempt", minWidth: 200, align: 'center', disableSorting: true, renderCell: ({ row }) => row?.mostRecentAttemptAt ? new Date(row.mostRecentAttemptAt).toLocaleString() : '-' },
    ];
  }

  if (String(interviewType || '').startsWith('coding')) {
    return [
      ...base,
      { field: "coding", label: "Coding", ...commonProperties, minWidth: 110, disableSorting: true },
      { field: "coreConcepts", label: "Core Concepts", ...commonProperties, minWidth: 140, disableSorting: true },
      { field: "criticalThinking", label: "Critical Thinking", ...commonProperties, minWidth: 150, disableSorting: true },
      { field: "content", label: "Content", ...commonProperties, minWidth: 110, disableSorting: true },
      { field: "speech", label: "Speech", ...commonProperties, minWidth: 110, disableSorting: true },
      { field: "finishedAttempts", label: "Finished Attempts", ...commonProperties, minWidth: 150, disableSorting: true },
      { field: "unfinishedAttempts", label: "Unfinished Attempts", ...commonProperties, minWidth: 160, disableSorting: true },
      { field: "averageTimeTaken", label: "Avg. Time Taken (min)", minWidth: 180, align: 'center', renderCell: ({ row }) => formatNumberOrDash(row?.averageTimeTaken, { decimals: 1 }), type: 'number', disableSorting: true },
      { field: "averageViolationCount", label: "Avg. Violations", ...commonProperties, minWidth: 140, disableSorting: true },
      { field: "averageHintUsed", label: "Avg. Hints Used", ...commonProperties, minWidth: 150, disableSorting: true },
      { field: "uniqueChallengeAttempted", label: "Unique Challenges", ...commonProperties, minWidth: 160, disableSorting: true },
      { field: "logicalReasoningAndAnalysis", label: "Logic & Analysis", ...commonProperties, minWidth: 160, disableSorting: true },
      { field: "edgeCaseHandling", label: "Edge Cases", ...commonProperties, minWidth: 130, disableSorting: true },
      { field: "codeOrganizationAndClarity", label: "Code Clarity", ...commonProperties, minWidth: 140, disableSorting: true },
      { field: "dsaUsageAndUnderstanding", label: "DSA Usage", ...commonProperties, minWidth: 130, disableSorting: true },
      { field: "conceptualClarity", label: "Conceptual Clarity", ...commonProperties, minWidth: 160, disableSorting: true },
      { field: "explanationAndLogicalCoherence", label: "Explanation & Coherence", ...commonProperties, minWidth: 200, disableSorting: true },
      { field: "scalabilityAndEfficiency", label: "Scalability & Efficiency", ...commonProperties, minWidth: 190, disableSorting: true },
      { field: "adaptiveImprovement", label: "Adaptive Improvement", ...commonProperties, minWidth: 170, disableSorting: true },
      { field: "fluency", label: "Fluency", ...commonProperties, minWidth: 110, disableSorting: true },
      { field: "delivery", label: "Delivery", ...commonProperties, minWidth: 110, disableSorting: true },
      { field: "mostRecentAttemptAt", label: "Most Recent Attempt", minWidth: 200, align: 'center', renderCell: ({ row }) => row?.mostRecentAttemptAt ? new Date(row.mostRecentAttemptAt).toLocaleString() : '-', disableSorting: true },
    ];
  }

  // Assessment/Verbal columns (existing)
  return [
    ...base,
    { field: "overallContent", label: "Content", ...commonProperties, minWidth: 100, disableSorting: true },
    { field: "overallComm", label: "Speech", ...commonProperties, minWidth: 100, disableSorting: true },
    { field: "highestCommScore", label: "Highest Comm.", ...commonProperties, minWidth: 140, disableSorting: true },
    { field: "lowestCommScore", label: "Lowest Comm.", ...commonProperties, minWidth: 140, disableSorting: true },
    { field: "highestContentScore", label: "Highest Content", ...commonProperties, minWidth: 160, disableSorting: true },
    { field: "lowestContentScore", label: "Lowest Content", ...commonProperties, minWidth: 160, disableSorting: true },
    { field: "finishedAttempts", label: "Finished Attempts", ...commonProperties, minWidth: 140, disableSorting: true },
    { field: "unfinishedAttempts", label: "Unfinished Attempts", ...commonProperties, minWidth: 160, disableSorting: true },
    { field: "confidence", label: "Confidence", ...commonProperties, minWidth: 100, disableSorting: true },
    { field: "delivery", label: "Delivery", ...commonProperties, minWidth: 100, disableSorting: true },
    { field: "fluency", label: "Fluency", ...commonProperties, minWidth: 100, disableSorting: true },
    { field: "wordChoice", label: "Word choice", ...commonProperties, minWidth: 120, disableSorting: true },
    { field: "similarity", label: "Similarity", ...commonProperties, minWidth: 100, disableSorting: true },
    { field: "relevance", label: "Relevance", ...commonProperties, minWidth: 110, disableSorting: true },
    { field: "logicFlow", label: "Logic flow", ...commonProperties, minWidth: 120, disableSorting: true },
    { field: "averageTimeTaken", label: "Avg. Time Taken (min)", minWidth: 160, align: 'center', renderCell: ({ row }) => formatNumberOrDash(row?.averageTimeTaken, { decimals: 1 }), type: 'number', disableSorting: true },
    { field: "assessmentAttempted", label: "Assessments Attempted", ...commonProperties, minWidth: 200, disableSorting: true },
    { field: "assessmentAvailable", label: "Assessments Available", ...commonProperties, minWidth: 200, disableSorting: true },
    { field: "averageQuestionAnswered", label: "Avg. Qs Answered", minWidth: 160, align: 'center', renderCell: ({ row }) => formatNumberOrDash(row?.averageQuestionAnswered, { decimals: 1 }), type: 'number', disableSorting: true },
    { field: "mostRecentAttemptAt", label: "Most Recent Attempt", minWidth: 200, align: 'center', renderCell: ({ row }) => row?.mostRecentAttemptAt ? new Date(row.mostRecentAttemptAt).toLocaleString() : '-', disableSorting: true },
  ];
};

function UsersAnalytics({ batch, template , forTemplateAnalytics, interviewType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // Question-wise analysis is a verbal/coding concept; a case attempt is one
  // continuous transcript, so that export mode is hidden for it.
  const isCaseInterview = String(interviewType || '').startsWith('case');

  const [rows, setRows] = useState([]);
  const [exportRows, setExportRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Show all columns by default in StaticTable (users can hide via Category)
  const initialFilters = React.useMemo(() => buildColumns(interviewType).map(c => c.field), [interviewType]);

  const fetchRows = React.useCallback(async ({ page = currentPage, filterByName } = {}) => {
    if (!id) return;
    setLoading(true);
    try {
      const params = { 
        page, 
        pageSize: itemsPerPage, 
        interviewVariantType: interviewType 
      };
      if (filterByName) params.filterByName = filterByName;
      const data = await getInterviewPerformance(id, interviewType, params);
      const performance = data?.performance || data?.usersPerformance || [];
      setRows(performance);
      setTotalItems(data?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id, interviewType, currentPage, itemsPerPage]);

  React.useEffect(() => {
    fetchRows({ page: 1 });
  }, [id, interviewType]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRows({ page: newPage, filterByName: searchText });
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const params = {
        interviewVariantType: interviewType,
        toExport: true
      };
      if (searchText) params.filterByName = searchText;
      const data = await getInterviewPerformance(id, interviewType, params);
      const performance = data?.performance || data?.usersPerformance || [];
      setExportRows(performance);
      return performance;
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (rowSelectionModel && rowSelectionModel.length > 0) {
      navigate(`/students/${rowSelectionModel[0]}`);
    }
  }, [rowSelectionModel]);

  // Verbal and coding rows have no overall score of their own, so it is derived
  // here from the communication/content pair. Case rows arrive with a real
  // overallScore already computed against the rubric weights — recomputing it
  // from fields they don't have would blank the column out.
  const withOverallScore = React.useCallback((rowData, index) => {
    const id = rowData._id || rowData.id || `temp-id-${index}`;

    if (rowData.overallScore !== undefined && rowData.overallScore !== null) {
      return { ...rowData, id };
    }

    const overallScore = getAverageScore(rowData.overallComm, rowData.overallContent);

    return {
      ...rowData,
      id,
      overallScore: isNaN(overallScore) || overallScore === null ? "-" : overallScore
    };
  }, []);

  const _rows = React.useMemo(
    () => rows?.map(withOverallScore),
    [rows, withOverallScore]
  );

  const _exportRows = React.useMemo(
    () => exportRows?.map(withOverallScore),
    [exportRows, withOverallScore]
  );

  // Pagination logic
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;


  const getActions = [
    {
      title: "View Performance",
        renderIcon:(param)=> 
          <IconButton 
            variant='contained'
            style={{
                      border : `1px solid ${theme.palette.primary.main}`,
                      padding : theme.spacing(1)
                  }}
            >
              <img 
                src='https://assets.languify.in/images/PerformanceIcon.svg' 
                alt='performance' 
              />
            </IconButton>,
      label: "View Performance",
      onClick: (param) => navigate(`/students/${param.id}`)
    }
  ];

  return (
    <>
      <Box mb={7} mt={2} display='flex' flexDirection='column' gap='4px'>
        <Typography variant="h4-medium">
          Students performance
        </Typography>
        <Typography variant="h6-medium" color='neutral.clr-600'>
          View students' performance in this batch
        </Typography>
      </Box>

      <StaticTable
        columns={buildColumns(interviewType)}
        loading={loading}
        actions={getActions}
        rows={_rows}
        exportRows={_exportRows}
        initialFilters={initialFilters}
        noRowsMessage="No students found in this batch!!"
        sortByColumns={
          [
            { field: 'overallScore', sortByTitle: "Scores: High to Low", sortBy: 'desc' },
            { field: 'overallScore', sortByTitle: "Scores: Low to High", sortBy: 'asc' },
            { field: 'finishedAttempts', sortByTitle: "Engagement: High to Low", sortBy: 'desc' },
            { field: 'finishedAttempts', sortByTitle: "Engagement: Low to High", sortBy: 'asc' }
          ]
        }
        exportByAttempts={!isCaseInterview}
        rowSelectionModel={rowSelectionModel}
        setRowSelectionModel={setRowSelectionModel}
        useItemProps={{
          name: template.name,
          exportService: exportTemplateAnalyticsExport,
          payload: template,
          onExport: handleExport,
        }}
        forTemplateAnalytics={forTemplateAnalytics}
        onSearch={(value) => { 
          setSearchText(value); 
          setCurrentPage(1);
          fetchRows({ page: 1, filterByName: value }); 
        }}
        outerSearchString={searchText}
      />

      
      
      {/* Pagination Component */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
          <Typography variant="h6-medium" color="neutral.clr-500" display="flex" gap="4px">
            Showing 
            <Typography variant="h6-medium" color="neutral.clr-900">
              {Math.min(startIndex + 1, totalItems)}-{Math.min(endIndex, totalItems)}
            </Typography> 
            of 
            <Typography variant="h6-medium" color="neutral.clr-900">
              {totalItems}
            </Typography> 
            entries
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            setPage={handlePageChange}
          />
        </Box>
      )}
    </>
  );
}

export default UsersAnalytics;
